export interface User {
  id: string;
  displayName: string;
  email: string;
}

export interface Conversation {
  id: string;
  title: string;
  status: 'refining' | 'created';
  ticketKey?: string;
  ticketUrl?: string;
  updatedAt: string;
}

export async function fetchMe(): Promise<User | null> {
  const res = await fetch('/api/me', { credentials: 'include' });
  if (!res.ok) return null;
  return res.json();
}

export async function fetchConversations(): Promise<Conversation[]> {
  const res = await fetch('/api/conversations', { credentials: 'include' });
  if (!res.ok) throw new Error('Error cargando conversaciones');
  return res.json();
}

export async function createConversation(title?: string): Promise<Conversation> {
  const res = await fetch('/api/conversations', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ title }),
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Error creando conversación');
  return res.json();
}

export async function logout(): Promise<void> {
  const res = await fetch('/auth/logout', {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Error cerrando sesión');
}

export interface TicketDraft {
  summary: string;
  description: string;
  issueType: string;
}

export interface CreateTicketInput {
  epicKey?: string;
  summary: string;
  description: string;
  issueType: string;
  relatedKeys?: string[];
}

export async function generateTicketDraft(conversationId: string): Promise<TicketDraft> {
  const res = await fetch(`/api/conversations/${conversationId}/ticket-draft`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Error generando la vista previa del ticket');
  return res.json();
}

export async function createTicket(
  conversationId: string,
  input: CreateTicketInput
): Promise<{ key: string; url: string; title: string }> {
  const res = await fetch(`/api/conversations/${conversationId}/create-ticket`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Error creando ticket');
  return res.json();
}

export interface UpdateTicketInput {
  summary: string;
  description: string;
  issueType?: string;
}

export async function generateTicketUpdateDraft(conversationId: string): Promise<TicketDraft> {
  const res = await fetch(`/api/conversations/${conversationId}/ticket-update-draft`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Error generando la vista previa de la actualización');
  return res.json();
}

export async function updateTicket(
  conversationId: string,
  input: UpdateTicketInput
): Promise<{ key: string; url: string; spec: string }> {
  const res = await fetch(`/api/conversations/${conversationId}/update-ticket`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Error actualizando el ticket');
  return res.json();
}

export async function renameConversation(conversationId: string, title: string): Promise<void> {
  const res = await fetch(`/api/conversations/${conversationId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ title }),
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Error renombrando conversación');
}

export async function cloneConversation(conversationId: string): Promise<Conversation> {
  const res = await fetch(`/api/conversations/${conversationId}/clone`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Error clonando conversación');
  return res.json();
}

export interface Epic {
  key: string;
  summary: string;
}

export async function fetchEpics(): Promise<Epic[]> {
  const res = await fetch('/api/epics', { credentials: 'include' });
  if (!res.ok) throw new Error('Error cargando épicas');
  return res.json();
}

export interface IssueType {
  id: string;
  name: string;
}

export async function fetchIssueTypes(): Promise<IssueType[]> {
  const res = await fetch('/api/issue-types', { credentials: 'include' });
  if (!res.ok) throw new Error('Error cargando tipos de issue');
  return res.json();
}

export interface IssueSummary {
  key: string;
  summary: string;
}

export async function searchIssues(query: string): Promise<IssueSummary[]> {
  const res = await fetch(`/api/issues/search?q=${encodeURIComponent(query)}`, { credentials: 'include' });
  if (!res.ok) throw new Error('Error buscando issues');
  return res.json();
}

export interface StoredMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  createdAt: string;
}

export async function fetchMessages(conversationId: string): Promise<StoredMessage[]> {
  const res = await fetch(`/api/conversations/${conversationId}/messages`, { credentials: 'include' });
  if (!res.ok) throw new Error('Error cargando mensajes');
  return res.json();
}

export function streamMessage(
  conversationId: string,
  content: string,
  onChunk: (chunk: string) => void,
  onDone: () => void,
  onError: (error: string) => void
): void {
  // EventSource no soporta POST con body, así que usamos fetch + ReadableStream
  fetch(`/api/conversations/${conversationId}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ content }),
    credentials: 'include',
  }).then(async (res) => {
    if (!res.body) {
      onError('No hay respuesta del servidor');
      return;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith('data: ')) continue;
        const data = trimmed.slice(6);
        try {
          const parsed = JSON.parse(data);
          if (parsed.chunk) onChunk(parsed.chunk);
          if (parsed.done) onDone();
          if (parsed.error) onError(parsed.error);
        } catch {
          // ignore
        }
      }
    }
    onDone();
  }).catch((err) => {
    onError(err.message);
  });
}
